import { useHead } from '@unhead/vue';
import { computed, type Ref } from 'vue';

import type { Customer, EventDetail, Venue } from '@/types';
import { getLocalizedEventValue } from '@/utils/eventItemUtil';

function removeServerStructuredData(type: 'event' | 'organization') {
  document.querySelectorAll(`[data-of2-structured-data="${type}"]`).forEach(element => {
    element.remove();
  });
}

function serverStructuredDataUrl(type: 'event' | 'organization') {
  const element = document.querySelector(`[data-of2-structured-data="${type}"]`);
  if (!element?.textContent) return null;

  try {
    const structuredData = JSON.parse(element.textContent) as { offers?: { url?: string }; url?: string };
    return type === 'event' ? structuredData.offers?.url : structuredData.url;
  } catch {
    return null;
  }
}

function currentStructuredDataUrl() {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Injects schema.org/Event JSON-LD structured data into the document head.
 * Only emits when the event is not a closed event.
 */
export function useEventStructuredData(
  eventData: Ref<EventDetail | null>,
  venue: Ref<Venue | null>,
  customer: Ref<Customer | undefined>,
  locale: Ref<string>,
) {
  useHead(computed(() => {
    removeServerStructuredData('organization');

    const event = eventData.value;
    if (!event || event.closed_event) return {};

    const customerObj = customer.value;
    if (!customerObj) return {};

    // Event time
    const eventTime = event.eventtime
      ? new Date(event.eventtime * 1000).toISOString()
      : null;
    if (!eventTime) return {};

    // Images
    const photoLocation = '/filestore/';
    const images: string[] = [];
    if (event.photo) {
      images.push(`${window.location.origin}${photoLocation}${event.photo}`);
    } else {
      images.push(`${window.location.origin}/b/sft2/images/img-04.jpg`);
    }
    if (event.extra_photos) {
      for (const photo of event.extra_photos) {
        images.push(`${window.location.origin}${photoLocation}${photo}`);
      }
    }

    // Availability
    const availability = event.soldout
      ? 'https://schema.org/SoldOut'
      : 'https://schema.org/InStock';

    // Price (stored in øre/cents)
    const price = event.price != null ? (event.price / 100).toString() : '0';
    const priceCurrency = event.currency || 'DKK';

    // Event info
    const eventName = getLocalizedEventValue(event, locale.value, 'info1') || 'N/A';
    const eventDescription = getLocalizedEventValue(event, locale.value, 'info2') || 'N/A';

    // Venue
    const venueObj = venue.value;
    const venueName = venueObj?.name || ' ';
    const venueAddress = venueObj?.address || ' ';
    const venueCity = venueObj?.city || ' ';
    const venueZip = venueObj?.zip || ' ';
    const venueCountry = venueObj?.country || ' ';

    // Customer/performer name
    let performerName = customerObj.name;
    if (customerObj.structured_data_name_override) {
      performerName = customerObj.structured_data_name_override;
    } else if (customerObj.alias) {
      performerName = customerObj.alias;
    }

    const structuredData: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      'name': eventName,
      'startDate': eventTime,
      'endDate': eventTime,
      'location': {
        '@type': 'Place',
        'name': venueName,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': venueAddress,
          'addressLocality': venueCity,
          'postalCode': venueZip,
          'addressRegion': ' ',
          'addressCountry': venueCountry,
        },
      },
      'image': images,
      'description': eventDescription,
      'offers': {
        '@type': 'Offer',
        'url': window.location.href,
        'price': price,
        'priceCurrency': priceCurrency,
        'availability': availability,
        'validFrom': eventTime,
      },
      'performer': {
        '@type': 'Organization',
        'name': performerName,
      },
    };

    // Add performer description if override is set
    if (customerObj.structured_data_description_override) {
      (structuredData['performer'] as Record<string, unknown>)['description'] =
        customerObj.structured_data_description_override;
    }

    const serverUrl = serverStructuredDataUrl('event');
    if (serverUrl === currentStructuredDataUrl()) return {};
    if (serverUrl) removeServerStructuredData('event');

    return {
      script: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
      ],
    };
  }));
}

/**
 * Injects schema.org/Organization JSON-LD structured data into the document head.
 */
export function useOrganizationStructuredData(
  customer: Ref<Customer | undefined>,
) {
  useHead(computed(() => {
    removeServerStructuredData('event');

    const customerObj = customer.value;
    if (!customerObj) return {};

    // Customer/organization name
    let organizationName = customerObj.name;
    if (customerObj.structured_data_name_override) {
      organizationName = customerObj.structured_data_name_override;
    } else if (customerObj.alias) {
      organizationName = customerObj.alias;
    }

    const structuredData: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': organizationName,
      'url': window.location.href,
    };

    if (customerObj.structured_data_description_override) {
      structuredData['description'] = customerObj.structured_data_description_override;
    }

    const serverUrl = serverStructuredDataUrl('organization');
    if (serverUrl === currentStructuredDataUrl()) return {};
    if (serverUrl) removeServerStructuredData('organization');

    return {
      script: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
      ],
    };
  }));
}
